import React, { useState, useMemo } from 'react';
import { OpportunityItem, TechnicalStatus, OpportunityStatus } from '../models/opportunity';
import { OpportunityCard } from './OpportunityCard';
import { NovaOportunidadeModal, NovaOportunidadeForm } from './NovaOportunidadeModal';

interface Membro { nome: string; email: string; }
type Filter = 'mine_action' | 'mine_all' | 'all' | 'execution' | 'won';
type SortBy = 'sla' | 'fase' | 'cliente' | 'valor' | 'criacao';

interface PipelineBoardProps {
  opportunities: OpportunityItem[];
  currentUserEmail: string;
  currentUserName: string;
  onUpdateReview: (id: string, status: TechnicalStatus, parecer: string, historicoActual: string, novoStatusOpp?: OpportunityStatus) => Promise<void>;
  onCreateOpportunity: (form: NovaOportunidadeForm) => Promise<void>;
  onAdvanceStatus: (id: string, newStatus: OpportunityStatus, extra?: Record<string, any>) => Promise<void>;
  onUpdateExecution: (id: string, progresso: number, entrada: string, diarioActual: string) => Promise<void>;
  getMembers: () => Promise<Membro[]>;
  getNextCode: () => Promise<string>;
  onExport: () => void;
  initialFilter?: Filter;
}

const TECNICO_TURN_STATUSES = [
  '2. Levantamento de Necessidades',
  '3. Especificação Técnica',
  '8. Em Execução',
  '9. Entrega & Validação',
];

function sortItems(list: OpportunityItem[], sortBy: SortBy): OpportunityItem[] {
  return [...list].sort((a, b) => {
    switch (sortBy) {
      case 'sla': {
        const da = a.dataLimiteParecer ? new Date(a.dataLimiteParecer).getTime() : Infinity;
        const db = b.dataLimiteParecer ? new Date(b.dataLimiteParecer).getTime() : Infinity;
        return da - db;
      }
      case 'fase': {
        const fa = parseInt(a.statusOportunidade.split('.')[0], 10);
        const fb = parseInt(b.statusOportunidade.split('.')[0], 10);
        return fa - fb;
      }
      case 'cliente':
        return a.cliente.localeCompare(b.cliente);
      case 'valor':
        return (b.valorEstimado ?? 0) - (a.valorEstimado ?? 0);
      case 'criacao':
      default:
        return a.codigoOportunidade.localeCompare(b.codigoOportunidade);
    }
  });
}

// Calculate total pipeline value
function pipelineValue(list: OpportunityItem[]): number {
  return list.reduce((acc, o) => acc + (o.valorEstimado ?? 0), 0);
}

export const PipelineBoard: React.FC<PipelineBoardProps> = ({
  opportunities, currentUserEmail, currentUserName,
  onUpdateReview, onCreateOpportunity, onAdvanceStatus, onUpdateExecution,
  getMembers, getNextCode, onExport, initialFilter,
}) => {
  const [filter, setFilter] = useState<Filter>(initialFilter || 'mine_action');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('sla');
  const [showNova, setShowNova] = useState(false);

  React.useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);

  const email = currentUserEmail.toLowerCase();
  const isTecnicoTurn = (o: OpportunityItem) => TECNICO_TURN_STATUSES.includes(o.statusOportunidade);

  const myActionItems = useMemo(() => opportunities.filter(o => {
    const isMyComercial = o.comercialEmail?.toLowerCase() === email;
    const isMyTecnico   = o.responsavelTecnicoEmail?.toLowerCase() === email;
    const tecnicoTurn   = isTecnicoTurn(o);
    const isTerminal    = ['10. Concluído / Ganho', '11. Perdido / Cancelado'].includes(o.statusOportunidade);
    if (isTerminal) return false;
    if (isMyTecnico && tecnicoTurn) return true;
    if (isMyComercial && !tecnicoTurn) return true;
    return false;
  }), [opportunities, email]);

  const myAllItems = useMemo(() => opportunities.filter(o =>
    o.comercialEmail?.toLowerCase() === email ||
    o.responsavelTecnicoEmail?.toLowerCase() === email
  ), [opportunities, email]);

  const filtered = useMemo(() => {
    let list =
      filter === 'mine_action' ? myActionItems :
      filter === 'mine_all'   ? myAllItems :
      filter === 'execution'  ? opportunities.filter(o => o.statusOportunidade === '8. Em Execução') :
      filter === 'won'        ? opportunities.filter(o => o.statusOportunidade === '10. Concluído / Ganho') :
      opportunities;

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        o.codigoOportunidade.toLowerCase().includes(q) ||
        o.cliente.toLowerCase().includes(q) ||
        (o.comercialNome || '').toLowerCase().includes(q) ||
        (o.responsavelTecnicoNome || '').toLowerCase().includes(q) ||
        (o.ambitoNecessidades || '').toLowerCase().includes(q)
      );
    }

    return sortItems(list, sortBy);
  }, [opportunities, filter, search, sortBy, myActionItems, myAllItems]);

  const totalValor = pipelineValue(filtered.filter(o => !['10. Concluído / Ganho', '11. Perdido / Cancelado'].includes(o.statusOportunidade)));

  const tabStyle = (t: Filter, color = '#CC2936'): React.CSSProperties => ({
    padding: '13px 15px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    color: filter === t ? 'var(--chk-white)' : 'var(--chk-muted)',
    background: 'none', border: 'none',
    borderBottom: `2px solid ${filter === t ? color : 'transparent'}`,
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'all .15s', whiteSpace: 'nowrap',
  });

  const badge = (n: number, color: string) => n > 0
    ? <span style={{ background: color, color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{n}</span>
    : null;

  const emptyMsg = () => {
    if (filter === 'mine_action') return { icon: '✅', msg: 'Está tudo em dia! Sem acções pendentes.' };
    if (filter === 'won') return { icon: '🏆', msg: 'Ainda não há projectos concluídos.' };
    if (filter === 'execution') return { icon: '⚙️', msg: 'Nenhum projecto em execução.' };
    return { icon: '📭', msg: 'Sem oportunidades neste filtro.' };
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--chk-dark)' }}>
      {showNova && (
        <NovaOportunidadeModal
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          onSubmit={onCreateOpportunity}
          onClose={() => setShowNova(false)}
          getMembers={getMembers}
          getNextCode={getNextCode}
        />
      )}

      {/* ── Tabs + Nova button ── */}
      <div style={{
        padding: '0 24px', background: 'var(--chk-navy)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid var(--chk-border)', overflowX: 'auto',
      }}>
        <div style={{ display: 'flex' }}>
          <button style={tabStyle('mine_action', '#CC2936')} onClick={() => setFilter('mine_action')}>
            🎯 A minha vez {badge(myActionItems.length, '#CC2936')}
          </button>
          <button style={tabStyle('mine_all', '#CC2936')} onClick={() => setFilter('mine_all')}>
            👤 Minhas ({myAllItems.length})
          </button>
          <button style={tabStyle('execution', '#1755B8')} onClick={() => setFilter('execution')}>
            ⚙️ Em Execução {badge(opportunities.filter(o => o.statusOportunidade === '8. Em Execução').length, '#1755B8')}
          </button>
          <button style={tabStyle('won', '#22C55E')} onClick={() => setFilter('won')}>
            🏆 Concluídas
          </button>
          <button style={tabStyle('all')} onClick={() => setFilter('all')}>
            📋 Todas ({opportunities.length})
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 12 }}>
          <button onClick={onExport} title="Exportar para CSV" style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
            borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--chk-card)',
            color: 'var(--chk-muted)', border: '1px solid var(--chk-border)', cursor: 'pointer',
          }}>📥 CSV</button>
          <button onClick={() => setShowNova(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: 'var(--chk-red)', color: 'white', border: 'none', cursor: 'pointer',
          }}>➕ Nova</button>
        </div>
      </div>

      {/* ── Search + Sort + Pipeline Value ── */}
      <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 340, background: 'var(--chk-card)', border: '1px solid var(--chk-border)', borderRadius: 8, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--chk-muted)' }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cliente, código, técnico, âmbito..."
            style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--chk-text)', fontSize: 13, width: '100%' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--chk-muted)', cursor: 'pointer', fontSize: 14, padding: 0 }}>✕</button>
          )}
        </div>

        {/* Sort control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--chk-muted)' }}>Ordenar:</span>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as SortBy)}
            style={{ background: 'var(--chk-card)', border: '1px solid var(--chk-border)', color: 'var(--chk-text)', borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
          >
            <option value="sla">⏰ SLA (urgência)</option>
            <option value="fase">📊 Fase</option>
            <option value="cliente">🏢 Cliente A→Z</option>
            <option value="valor">💰 Valor ↓</option>
            <option value="criacao">📋 Código</option>
          </select>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center' }}>
          {totalValor > 0 && (
            <div style={{ fontSize: 12, color: '#F59E0B', fontWeight: 600 }}>
              💰 Pipeline: ${totalValor.toLocaleString('pt-PT')}
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--chk-muted)' }}>
            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* ── Contextual guide bar ── */}
      {filter === 'mine_action' && myActionItems.length > 0 && (
        <div style={{ margin: '0 24px 12px', background: 'rgba(204,41,54,.07)', border: '1px solid rgba(204,41,54,.25)', borderRadius: 10, padding: '10px 16px', fontSize: 12, color: '#F87171' }}>
          💡 Estes são os cards onde a tua acção é necessária agora. Clica no botão de acção em cada card para avançar.
        </div>
      )}

      {/* ── Grid ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 14, padding: '0 24px 24px',
      }}>
        {filtered.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--chk-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{emptyMsg().icon}</div>
            <div style={{ fontSize: 16 }}>{emptyMsg().msg}</div>
          </div>
        ) : (
          filtered.map(item => (
            <OpportunityCard
              key={item.id}
              item={item}
              currentUserEmail={currentUserEmail}
              currentUserName={currentUserName}
              onUpdate={onUpdateReview}
              onAdvanceStatus={onAdvanceStatus}
              onUpdateExecution={onUpdateExecution}
            />
          ))
        )}
      </div>
    </div>
  );
};
